import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import Row from '@amzn/meridian/row';
import Column from '@amzn/meridian/column';
import Button from '@amzn/meridian/button';
import Alert from '@amzn/meridian/alert';
import RadioButton from '@amzn/meridian/radio-button';
import Loader from '@amzn/meridian/loader';
import Text from '@amzn/meridian/text';
import Heading from '@amzn/meridian/heading';
import Divider from '@amzn/meridian/divider';
import Menu, { MenuItem } from '@amzn/meridian/menu';
import Icon from '@amzn/meridian/icon';
import Pagination from '@amzn/meridian/pagination';
import downloadLargeTokens from '@amzn/meridian-tokens/base/icon/download-large';
import caretDownTokens from '@amzn/meridian-tokens/base/icon/caret-down';
import refreshTokens from '@amzn/meridian-tokens/base/icon/refresh';
import DataTable, {
    DataTableBody,
    DataTableHeader,
    DataTableHead,
    DataTableCell,
    DataTableRow,
} from '@amzn/meridian/data-table';
import { getBatchEvents, getPresignedDownloadUrls } from '../services/api';
import { BATCH_EVENT_TABLE } from '../appConfig';
import { S3_KEYS, resolveS3Key } from '../constants/s3Keys';

interface BatchEvent {
    batch_id: string;
    user: string;
    ofd_date: string;
    horizon: string;
    round: string;
    created_at: string;
    inserted_at: string;
    cap_setting_status: string;
    cycle_transfer_status: string;
    cap_release_status: string;
    cap_upload_status: string;
}

const STAGE_LABELS: Record<string, string> = {
    cap_setting: 'Cap Setting',
    cycle_transfer: 'Cycle Transfer',
    cap_release: 'Cap Release',
};

const ControlRoom: React.FC = () => {
    const [data, setData] = useState<BatchEvent[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [selectedBatch, setSelectedBatch] = useState<BatchEvent | null>(null);
    const [sortColumn, setSortColumn] = useState<string>('created_at');
    const [sortDirection, setSortDirection] = useState<'ascending' | 'descending' | ''>('descending');
    const [downloadMenuOpen, setDownloadMenuOpen] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const downloadButtonRef = useRef<HTMLElement>(null);
    const PAGE_SIZE = 25;

    const loadData = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const result = await getBatchEvents(BATCH_EVENT_TABLE, 'AMZL');
            const items = Array.isArray(result?.items || result) ? (result?.items || result) : [];
            const sorted = items.sort((a: any, b: any) =>
                new Date(b.created_at || b.inserted_at).getTime() - new Date(a.created_at || a.inserted_at).getTime()
            );
            setData(sorted);
        } catch (err: any) {
            setError(err.message || 'Failed to load batch events');
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => { loadData(); }, [loadData]);

    const onSort = useCallback(({ sortColumn: col, sortDirection: dir }: any) => {
        setSortColumn(col);
        setSortDirection(dir);
    }, []);

    const sortedData = useMemo(() => {
        if (!sortColumn) return data;
        return [...data].sort((a, b) => {
            const aVal = (a as any)[sortColumn] || '';
            const bVal = (b as any)[sortColumn] || '';
            const cmp = aVal.localeCompare(bVal, undefined, { numeric: true });
            return sortDirection === 'ascending' ? cmp : -cmp;
        });
    }, [data, sortColumn, sortDirection]);

    const numberOfPages = Math.ceil(sortedData.length / PAGE_SIZE);
    const paginatedData = sortedData.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

    const columns = [
        { id: 'batch_id', label: 'Batch ID' },
        { id: 'user', label: 'User' },
        { id: 'ofd_date', label: 'OFD Date' },
        { id: 'horizon', label: 'Horizon' },
        { id: 'round', label: 'Round' },
        { id: 'created_at', label: 'Created At' },
        { id: 'cap_setting_status', label: 'Cap Setting' },
        { id: 'cycle_transfer_status', label: 'Cycle Transfer' },
        { id: 'cap_release_status', label: 'Cap Release' },
        { id: 'cap_upload_status', label: 'Cap Upload' },
    ];

    const getDownloadItems = useCallback(() => {
        if (!selectedBatch) return [];
        const items: { label: string; stage: string; type: string; key: string }[] = [];
        const horizon = selectedBatch.horizon || 'D-1';
        const program = 'AMZL';
        const horizonKeys = S3_KEYS[program]?.[horizon];
        if (!horizonKeys) return items;

        const stages = ['cap_setting', 'cycle_transfer', 'cap_release'] as const;
        for (const stage of stages) {
            const statusKey = `${stage}_status` as keyof BatchEvent;
            if (selectedBatch[statusKey] !== 'COMPLETED') continue;

            const stageKeys = horizonKeys[stage as keyof typeof horizonKeys];
            if (!stageKeys) continue;

            const inputKeys = stageKeys.inputs;
            const outputKeys = stageKeys.outputs;
            for (const key in inputKeys) {
                if (inputKeys.hasOwnProperty(key)) {
                    items.push({ label: `📥 ${STAGE_LABELS[stage]} - ${key.replace(/_/g, ' ')}`, stage, type: 'inputs', key });
                }
            }
            for (const key in outputKeys) {
                if (outputKeys.hasOwnProperty(key)) {
                    items.push({ label: `📤 ${STAGE_LABELS[stage]} - ${key.replace(/_/g, ' ')}`, stage, type: 'outputs', key });
                }
            }
        }
        return items;
    }, [selectedBatch]);

    const handleDownload = async (stage: string, type: string, key: string) => {
        if (!selectedBatch) return;
        try {
            const horizon = selectedBatch.horizon || 'D-1';
            const program = 'AMZL';
            const horizonKeys = S3_KEYS[program]?.[horizon];
            if (!horizonKeys) return;
            const stageKeys = horizonKeys[stage as keyof typeof horizonKeys];
            if (!stageKeys) return;

            const template = type === 'inputs' ? stageKeys.inputs[key] : stageKeys.outputs[key];
            if (!template) return;

            const batchId = selectedBatch.batch_id;
            const round = selectedBatch.round || 'R1';
            const s3Key = resolveS3Key(template, { batchId, round });

            const { urls } = await getPresignedDownloadUrls([s3Key]);
            if (urls[s3Key]) window.open(urls[s3Key], '_blank');
        } catch (err: any) {
            setError(err.message || 'Download failed');
        }
    };

    const downloadItems = getDownloadItems();

    return (
        <Column spacing="400">
            <Heading level={4} type="h300">Control Room - AMZL Operations</Heading>

            <Alert type="neutral" size="medium">
                This page provides centralized control and monitoring for AMZL capacity management processes.
            </Alert>

            <Row spacing="300" alignmentVertical="center" alignmentHorizontal="end">
                    <Button onClick={loadData} disabled={isLoading} type="tertiary">
                        <Icon tokens={refreshTokens}>Refresh</Icon>
                        {isLoading ? 'Refreshing...' : 'Refresh'}
                    </Button>
                    <Button
                        ref={downloadButtonRef}
                        onClick={() => setDownloadMenuOpen(!downloadMenuOpen)}
                        type="secondary"
                        disabled={!selectedBatch}
                    >
                        <Icon tokens={downloadLargeTokens}>Download</Icon>
                        Download
                        <Icon tokens={caretDownTokens} />
                    </Button>
                    {downloadButtonRef.current && (
                        <Menu
                            anchorNode={downloadButtonRef.current}
                            open={downloadMenuOpen}
                            onClose={() => setDownloadMenuOpen(false)}
                            position="bottom"
                        >
                            {downloadItems.length === 0 ? (
                                <Text type="b200" color="secondary" alignment="center">No files available</Text>
                            ) : (
                                <>
                                    {['cap_setting', 'cycle_transfer', 'cap_release'].map(stage => {
                                        const stageItems = downloadItems.filter(i => i.stage === stage);
                                        if (stageItems.length === 0) return null;
                                        return (
                                            <React.Fragment key={stage}>
                                                <Text type="b100" color="secondary" alignment="center">
                                                    {STAGE_LABELS[stage]}
                                                </Text>
                                                {stageItems.map(item => (
                                                    <MenuItem
                                                        key={`${item.stage}-${item.type}-${item.key}`}
                                                        onClick={() => {
                                                            handleDownload(item.stage, item.type, item.key);
                                                            setDownloadMenuOpen(false);
                                                        }}
                                                    >
                                                        {item.label}
                                                    </MenuItem>
                                                ))}
                                                <Divider />
                                            </React.Fragment>
                                        );
                                    })}
                                </>
                            )}
                        </Menu>
                    )}
            </Row>

            {error && <Alert type="error" size="medium" onClose={() => setError(null)}>{error}</Alert>}

            {isLoading ? (
                <Row alignmentHorizontal="center" spacingInset="450"><Loader size="small" /></Row>
            ) : (
                <>
                <DataTable
                    onSort={onSort}
                    sortColumn={sortColumn}
                    sortDirection={sortDirection || undefined}
                    size="small"
                    showRowDividers={false}
                    showColumnDividers={false}
                    showStripes={true}
                    showBorder={false}
                >
                    <DataTableHead>
                        <DataTableRow highlightOnHover={false}>
                            <DataTableHeader> </DataTableHeader>
                            {columns.map(col => (
                                <DataTableHeader key={col.id} sortColumn={col.id}>{col.label}</DataTableHeader>
                            ))}
                        </DataTableRow>
                    </DataTableHead>
                    <DataTableBody>
                        {paginatedData.map((item, idx) => (
                            <DataTableRow
                                key={`${item.batch_id}-${idx}`}
                                rowId={item.batch_id}
                                highlightOnHover={true}
                            >
                                <DataTableCell containsSingleFocusableChild={true}>
                                    <RadioButton
                                        checked={selectedBatch?.batch_id === item.batch_id}
                                        onChange={() => setSelectedBatch(item)}
                                        value={item.batch_id}
                                        name="batch-select"
                                        size="small"
                                        aria-label={`Select batch ${item.batch_id}`}
                                    />
                                </DataTableCell>
                                <DataTableCell>{item.batch_id || 'Unknown'}</DataTableCell>
                                <DataTableCell>{item.user || 'Unknown'}</DataTableCell>
                                <DataTableCell>{item.ofd_date || 'N/A'}</DataTableCell>
                                <DataTableCell>{item.horizon || 'N/A'}</DataTableCell>
                                <DataTableCell>{item.round || 'N/A'}</DataTableCell>
                                <DataTableCell>{item.created_at || item.inserted_at || 'N/A'}</DataTableCell>
                                <DataTableCell>{item.cap_setting_status || ''}</DataTableCell>
                                <DataTableCell>{item.cycle_transfer_status || ''}</DataTableCell>
                                <DataTableCell>{item.cap_release_status || ''}</DataTableCell>
                                <DataTableCell>{item.cap_upload_status || ''}</DataTableCell>
                            </DataTableRow>
                        ))}
                        {paginatedData.length === 0 && (
                            <DataTableRow highlightOnHover={false}>
                                <DataTableCell colSpan={columns.length + 1}>
                                    <Row alignmentHorizontal="center" spacingInset="400">
                                        <Text color="secondary">No batch events found</Text>
                                    </Row>
                                </DataTableCell>
                            </DataTableRow>
                        )}
                    </DataTableBody>
                </DataTable>
                {numberOfPages > 1 && (
                    <Row alignmentHorizontal="end">
                        <Pagination
                            currentPage={currentPage}
                            numberOfPages={numberOfPages}
                            onChange={setCurrentPage}
                            showSkipArrows={true}
                        />
                    </Row>
                )}
                </>
            )}
        </Column>
    );
};

export default ControlRoom;
